import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Permission } from '@core/models';
import { ContextStateService } from '@core/services/context-state.service';
import { DialogService } from '@core/services/dialog';
import { ModuleRouteContextService } from '@core/services/modules/module-route-context.service';
import { environment } from '@environment';
import { filter, from, map, Observable, of, switchMap, take } from 'rxjs';
import { getModuleLandingCommands } from './landing-page.utils';

export const landingPageGuard: CanActivateFn = route => {
    const contextStateService = inject(ContextStateService);
    const moduleRouteContextService = inject(ModuleRouteContextService);
    const dialogService = inject(DialogService);
    const router = inject(Router);
    return toObservable(contextStateService.isCurrentContextLoaded).pipe(
        filter(isLoaded => isLoaded),
        take(1),
        switchMap((): Observable<boolean | UrlTree> => {
            const userPermissions = contextStateService.currentUserPermissions();

            if (contextStateService.isLoggedUserSuperAdmin()) {
                return of(router.createUrlTree(['/admin/organizations']));
            }

            const routeModuleSlug = route.parent?.paramMap.get('module');
            if (environment.modularityEnabled && routeModuleSlug) {
                return of(getModuleLandingUrl(routeModuleSlug, contextStateService, router));
            }

            const lastModuleType = contextStateService.currentUser()?.lastModuleType ?? null;
            if (lastModuleType !== null && contextStateService.isModuleEnabled(lastModuleType)) {
                const lastModuleSlug = moduleRouteContextService.getSlugFromModuleType(lastModuleType);
                if (lastModuleSlug) {
                    return of(router.createUrlTree(['/', lastModuleSlug]));
                }
            }

            if (environment.modularityEnabled) {
                if (contextStateService.enabledModules().length === 0) {
                    return of(router.createUrlTree(['/user/profile']));
                }

                return from(import('@pages/module/module-picker/module-picker-dialog/module-picker-dialog.component')).pipe(
                    switchMap(module => {
                        const dialogRef = dialogService.openCenterDialog(module.ModulePickerDialogComponent, { disableClose: true });

                        return dialogRef.afterClosed().pipe(map(() => false));
                    })
                );
            }
            return of(getPermissionBasedLandingUrl(userPermissions, router));
        })
    );
};

function getModuleLandingUrl(moduleSlug: string, contextStateService: ContextStateService, router: Router): UrlTree {
    return router.createUrlTree(
        getModuleLandingCommands(
            moduleSlug,
            (contextStateService.selectedModule()?.projects.length ?? 0) > 0,
            contextStateService.currentUserPermissions(),
            contextStateService.currentModuleFeatures()
        )
    );
}

function getPermissionBasedLandingUrl(userPermissions: Permission[], router: Router): UrlTree {
    if (userPermissions.includes(Permission.EditAskemos)) {
        return router.createUrlTree(['/askemos']);
    }

    if (userPermissions.includes(Permission.ViewContacts)) {
        return router.createUrlTree(['/contacts']);
    }

    if (userPermissions.includes(Permission.ViewAnalytics)) {
        return router.createUrlTree(['/analytics']);
    }

    if (userPermissions.includes(Permission.Admin)) {
        return router.createUrlTree(['/organization']);
    }

    return router.createUrlTree(['/user']);
}
